import { flow, types } from 'mobx-state-tree'
import { getSubscriptionDetailsTxnV3 } from '../../network/getSubscriptionDetailsTxnV3'
import { getSubscriptions } from '../../network/getSubscriptions'
import { getSubsTxnDetailsV3 } from '../../network/getSubsTxnDetailsV3'
import { initRefund } from '../../network/initRefund'
import { transactionColumnParser } from '../../network/parsers/TransactionColumnParser'
import {
  apiTypes,
  fromDateTime,
  geRequestDateTimeRoundOff,
  getRefundSource,
  getSelectedDate,
  toDateTime,
  toIsoDate,
} from '../../util/util'
import {
  refundSubscriptionInitiateTableParser,
  refundSubscriptionStatusParser,
  subscriptionfilterTableParserData,
} from '../parser/view/TableParser'
import {
  initiateRefundStatusDataV2Subscription,
  initiateRefundTitleDataV2Subscription,
  paginationSettings,
  SubscriptionDate,
  subscriptionFields,
  subscriptionTitleData,
} from '../static/data'
import { customError } from '../static/strings'
import { RootStore } from '../store'
import { actionMenuStrings } from './../static/strings'
import { Pagination, ResponseType } from './Transaction'
import { FieldSetting } from './Transactions'

const PaymentDetails = types.model('PaymentDetails', {
  payerName: types.optional(types.string, ''),
  payerVpa: types.optional(types.string, ''),
  payerAccountNumber: types.optional(types.string, ''),
  payerBankIfsc: types.optional(types.string, ''),
  payerBankName: types.optional(types.string, ''),
  payerAccountType: types.optional(types.string, ''),
  payerBankId: types.optional(types.string, ''),
  payerCardType: types.optional(types.string, ''),
  payerCardScheme: types.optional(types.string, ''),
  payerCardProduct: types.optional(types.string, ''),
  payerCardName: types.optional(types.string, ''),
  payerCardMaskedNumber: types.optional(types.string, ''),
})
const PaymentHistory = types.model('PaymentHistory', {
  referenceId: types.optional(types.string, ''),
  refundStatus: types.optional(types.string, ''),
  refundAmount: types.optional(types.string, ''),
  paymentId: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  paymentMode: types.optional(types.string, ''),
  paymentDetails: types.optional(PaymentDetails, {}),
  amount: types.optional(types.string, ''),
  addons: types.optional(types.string, ''),
  refund: types.optional(types.string, ''),
  processed: types.optional(types.string, ''), // for entered refund amount
  transactionDate: types.optional(types.string, ''),
  transactionMode: types.optional(types.string, ''),
  transactionId: types.optional(types.string, ''),
  sourceSystem: types.optional(types.string, ''),
})

const SubscriptionTransactionsHistory = types.model('SubscriptionTransactionsHistory', {
  id: types.optional(types.string, ''),
  amount: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  transactionDate: types.optional(types.string, ''),
  referenceId: types.optional(types.string, ''),
  paymentHistory: types.array(PaymentHistory),
  invoice: types.optional(types.string, ''),
  methodType: types.optional(types.string, ''),
})

const Customer = types.model('Customer', {
  id: types.optional(types.string, ''), // payer.userid // jmUserId,  customer id
  name: types.optional(types.string, ''),
  email: types.optional(types.string, ''),
  mobile: types.optional(types.string, ''),
  externalId: types.optional(types.string, ''),
})
const Subscription = types.model('Subscription', {
  id: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  type: types.optional(types.number, 0),
  scheduledAt: types.optional(types.string, ''),
  frequency: types.optional(types.number, 0),
  scheduledEnd: types.optional(types.string, ''),
  amount: types.optional(types.number, 0),
  customer: types.optional(Customer, {}),
  createdAt: types.optional(types.string, ''),
  updatedAt: types.optional(types.string, ''),
  revocable: types.optional(types.boolean, false),
  ruleRecurrenceValue: types.optional(types.number, 0),
  jmSubsId: types.optional(types.string, ''),
  transactionsHistory: types.array(SubscriptionTransactionsHistory),
  trxPagination: types.optional(Pagination, {}),
  invoice: types.optional(types.string, ''),
  paymentMode: types.optional(types.string, ''),
})

const Position = types.model('position', {
  left: types.number,
  top: types.number,
  visible: types.boolean,
})

const DateRange = types.model('date', {
  fromDate: types.Date,
  toDate: types.Date,
  position: Position,
  dateLabel: types.string,
})

const SubscriptionHistory = types
  .model('SubscriptionHistory', {
    pagination: Pagination,
    subscriptions: types.array(Subscription),
    fieldSettings: types.array(FieldSetting),
    selectedSubscription: types.optional(types.string, ''),
    selectedDateInfo: DateRange,
  })
  .views((self) => ({
    getSubscriptionList(): any[] {
      const own = self
      const subscriptions = subscriptionfilterTableParserData(
        own.subscriptions,
        own.fieldSettings,
        subscriptionTitleData,
      )
      const size = subscriptions.length
      if (size === 1) {
        return []
      }
      return [...subscriptions.slice(0)]
    },
    getSubscriptionListDetails(id: string): any {
      return JSON.parse(JSON.stringify(self.subscriptions?.filter((item) => item.id === id)[0] ?? {}))
    },
    getSubscriptionFields(): any {
      const fieldsdata = self.fieldSettings.slice()
      return fieldsdata
    },
    getSelectedTransactions(): any[] {
      const selectedTransactions: any[] = []
      self.subscriptions.map((item) => {
        const txn = item
        item.transactionsHistory.map((_item) => {
          _item.paymentHistory.map((_item_) => {
            if (_item_.transactionId === self.selectedSubscription) {
              selectedTransactions.push(item)
            }
          })
        })
        return txn
      })
      const trxs = refundSubscriptionInitiateTableParser(selectedTransactions, self.selectedSubscription)
      return [initiateRefundTitleDataV2Subscription, ...trxs.slice(0, 1)] // remvoing header
    },
    getRefundStatus(): any[] {
      const selectedTransactions: any[] = []
      self.subscriptions.map((item) => {
        const txn = item
        item.transactionsHistory.map((_item) => {
          _item.paymentHistory.map((_item_) => {
            if (_item_.transactionId === self.selectedSubscription) {
              selectedTransactions.push(item)
            }
          })
        })
        return txn
      })
      const trxs = refundSubscriptionStatusParser(selectedTransactions, self.selectedSubscription)
      return [initiateRefundStatusDataV2Subscription, ...trxs] // remvoing header
    },
    selectAllSubscriptionFields(value: boolean): any {
      const own = self
      const newFields =
        own?.fieldSettings.map((item) => {
          if (item.isDisable) {
            return item
          } else {
            return {
              ...item,
              isChecked: value,
            }
          }
        }) ?? []
      return JSON.parse(JSON.stringify(newFields))
    },
  }))
  .actions((self) => ({
    getSubscriptionHistory: flow(function* getSubscriptionHistory() {
      try {
        const pageNumber = self.pagination.number === 0 ? 1 : self.pagination.number
        let fromDateTimeCopy = self.selectedDateInfo.fromDate ?? new Date(Date.now())
        let toDateTimeCopy = self.selectedDateInfo.toDate ?? new Date(Date.now())
        // console.log('fromDateTimeCopy', fromDateTimeCopy)
        // console.log('toDateTimeCopy', toDateTimeCopy)
        let finaleDates = { fromDate: fromDateTimeCopy, toDate: toDateTimeCopy }
        if (self.selectedDateInfo.dateLabel !== actionMenuStrings.customDate) {
          finaleDates = geRequestDateTimeRoundOff(
            { fromDate: fromDateTimeCopy, toDate: toDateTimeCopy },
            self.selectedDateInfo,
            apiTypes.SUBSCRIPTION_HISTORY,
          )
        }
        //console.log('test finaleDates', finaleDates)
        const size = self.pagination.size < 10 ? 10 : self.pagination.size
        const toDate = toIsoDate(
          new Date(
            toDateTime(new Date(finaleDates.toDate) ?? new Date(Date.now()))
              .split(' ')
              .join('T'),
          ),
        )
        const fromDate = toIsoDate(new Date(fromDateTime(new Date(finaleDates.fromDate)).split(' ').join('T')))
        const revocable = RootStore.transactions.filter.subscriptionRevocable
        const subscriptionStatus = RootStore.transactions.filter.subscriptionStatus
        const subscriptionStartDate = RootStore.transactions.filter.subscriptionStartDate
        const subscriptionEndDate = RootStore.transactions.filter.subscriptionEndDate
        var subscriptionSearch
        const subscriptionSearchType = RootStore.transactions.filter.subscriptionSearchType

        if (subscriptionSearchType === 'customerMobileNumber') {
          subscriptionSearch = { countryCode: '+91', number: RootStore.transactions.filter.subscriptionSearch }
        } else {
          subscriptionSearch = RootStore.transactions.filter.subscriptionSearch
        }
        const subscriptionDateType = RootStore.transactions.filter.subscriptionDateType
        const subscriptionPaymentMode = RootStore.transactions.filter.subscriptionPaymentMode

        const getRevocable = {
          Revocable: true,
          'Non-Revocable': false,
        }
        const paymentMode = {
          UPI: '589',
          Cards: '590',
        }

        const getCreatedDate = {
          createdAt: { fromDate: fromDate, toDate: toDate },
          [SubscriptionDate.created]: { fromDate: subscriptionStartDate, toDate: subscriptionEndDate },
        }

        const trxStatus = {
          Paused: 'SUSPENDED',
          Completed: 'FULFILLED',
          Active: 'ACTIVE',
          Cancelled: 'CANCELED',
          Failed: 'FAILED',
        }

        const replaceTrxStatus = subscriptionStatus.map((item: string) => {
          return trxStatus[item as keyof typeof trxStatus] ?? item
        })

        var requestObj = {
          merchantId: RootStore.user.selectedMid,
          startDateTime:
            subscriptionDateType === SubscriptionDate.starts
              ? { fromDate: subscriptionStartDate, toDate: subscriptionEndDate }
              : undefined,
          transactionStatus:
            subscriptionStatus.length === 0 || subscriptionStatus.includes('ALL') ? undefined : replaceTrxStatus,
          endDateTime:
            subscriptionDateType === SubscriptionDate.ending
              ? { fromDate: subscriptionStartDate, toDate: subscriptionEndDate }
              : undefined,
          createdAt: getCreatedDate[subscriptionDateType as keyof typeof getCreatedDate] ?? undefined,
          updatedAt:
            subscriptionDateType === SubscriptionDate.updated
              ? { fromDate: subscriptionStartDate, toDate: subscriptionEndDate }
              : undefined, // TODO: check
          [subscriptionSearchType]:
            subscriptionSearch === '' || subscriptionSearchType === '' ? undefined : subscriptionSearch,
          revocable: getRevocable[revocable as keyof typeof getRevocable] ?? undefined,
          instrumentTypeId: paymentMode[subscriptionPaymentMode as keyof typeof paymentMode] ?? undefined,
        }
        const request = {
          inputFilter: requestObj,
          configParams: {
            page: pageNumber, //page number
            recordCount: size, //per page ten records
            sortType: 'DESC',
            sortColumn: 'transactionDateTime',
          },
        }
        const response = yield getSubscriptions(
          request,
          RootStore.user.session.getTokens,
          RootStore.user.session.userAccessToken,
        )
        const own = self
        //Fix for multiple calls on paginating quickly
        if (pageNumber === 1) {
          own.pagination = JSON.parse(JSON.stringify(response?.pagination))
        }
        own.subscriptions = response.subscriptions
      } catch (error) {
        RootStore?.response?.setMessage(error?.message ?? customError.errorOccured)
      }
    }),
    fetchSubscriptionDetailsTxnV3: flow(function* fetchSubscriptionDetailsTxnV3(id: string, selectedPage: number) {
      try {
        const own = self
        let size
        own.subscriptions.forEach((item, index) => {
          if (item.id === id) {
            size = own.subscriptions[index].trxPagination.size < 10 ? 10 : own.subscriptions[index].trxPagination.size
          }
          return item
        })

        const request = {
          inputFilter: {
            merchantId: RootStore.user.selectedMid,
            subscriptionId: id,
            responseType: ResponseType.CUSTOM,
            transactionType: 'SUBSCRIPTION_TRANSACTION',
            paymentMethods: ['UPI', 'WALLET', 'DEBIT', 'CREDIT', 'NETBANKING'],
            transactionStatus: ['SUCCESS', 'FAILED'],
          },
          configParams: {
            mediaType: 'JSON',
            sortColumn: 'created',
            page: selectedPage, //page number
            recordCount: size, //per page ten records
            sortType: 'DESC',
          },
          outputDomains: ['transactionId', 'amount', 'status', 'transactionDate', 'referenceId', 'invoice'],
        }
        const response = yield getSubscriptionDetailsTxnV3(
          request,
          RootStore.user.session.getTokens,
          RootStore.user.session.userAccessToken,
        )

        own.subscriptions.forEach((item, index) => {
          if (item.id === id) {
            own.subscriptions[index].transactionsHistory = response.data
            own.subscriptions[index].trxPagination = response.page
          }
          return item
        })
      } catch (error) {
        RootStore?.response?.setMessage(error?.message ?? customError.errorOccured)
      }
    }),
    fetchSubsTxnDetailsV3: flow(function* fetchSubsTxnDetailsV3(subscriptionId: string, txnId: string) {
      try {
        const request = {
          inputFilter: {
            merchantId: RootStore.user.selectedMid,
            subscriptionId: subscriptionId,
            transactionId: [txnId],
            transactionType: 'SUBSCRIPTION_TRANSACTION_DETAIL',
            responseType: ResponseType.CUSTOM,
          },
          configParams: {
            mediaType: 'JSON',
            sortColumn: 'created',
            sortType: 'DESC',
          },
          outputDomains: [
            'referenceId',
            'paymentId',
            'status',
            'paymentmode',
            'paymentDetails',
            'amount',
            'addons',
            'refund',
            'internalId',
            'invoice',
            'transactionId',
            'transactionDate',
            'sourceSystem',
          ],
        }
        const response = yield getSubsTxnDetailsV3(
          request,
          RootStore.user.session.getTokens,
          RootStore.user.session.userAccessToken,
        )
        const own = self
        own.subscriptions.forEach((item, index) => {
          if (item.id === subscriptionId) {
            item.transactionsHistory.forEach((txnItem, i) => {
              if (txnItem.id === txnId) {
                own.subscriptions[index].transactionsHistory[i].paymentHistory = JSON.parse(JSON.stringify(response))
              }
            })
          }
          return item
        })
      } catch (error) {
        RootStore?.response?.setMessage(error?.message ?? customError.errorOccured)
      }
    }),
    updateSelectedDateInfo(dateObj: any): void {
      self.selectedDateInfo.fromDate = dateObj?.fromDate ?? new Date(Date.now())
      self.selectedDateInfo.toDate = dateObj?.toDate ?? new Date(Date.now())
      self.selectedDateInfo.position.top = dateObj?.position?.top ?? 0
      self.selectedDateInfo.position.left = dateObj?.position?.left ?? 0
      self.selectedDateInfo.position.visible = dateObj?.position?.visible ?? false
      self.selectedDateInfo.dateLabel = dateObj?.dateLabel ?? ''
    },
    getColumns() {
      const own = self
      const parsedColumn = transactionColumnParser({ data: { columns: subscriptionFields } })
      own.fieldSettings = JSON.parse(JSON.stringify([...parsedColumn]))
    },
    applySubFieldSettings(newSettings: string[]): void {
      const own = self
      const newFields =
        own?.fieldSettings.map((item) => {
          if (newSettings.includes(item?.id)) {
            item.isChecked = true
          } else {
            item.isChecked = false
          }
          return item
        }) ?? []
      own.fieldSettings = JSON.parse(JSON.stringify(newFields))
    },
    updateSelectedSubscription(rrn: string): void {
      const own = self
      own.selectedSubscription = JSON.parse(JSON.stringify(rrn))
    },
    updateRefundRequested(rrn: string, amount: string): void {
      const own = self
      const value = amount
      const trxn = own.subscriptions.map((item) => {
        const txn = item
        item.transactionsHistory.map((_item) => {
          _item.paymentHistory.map((_item_) => {
            if (_item_.transactionId === self.selectedSubscription) {
              _item_.processed = value
            }
          })
        })

        return txn
      })
      own.subscriptions = JSON.parse(JSON.stringify([...trxn]))
    },
    updateRefundStatus(rrn: string, status: string): void {
      const own = self
      const trxn = own.subscriptions.map((item) => {
        const txn = item
        item.transactionsHistory.map((_item) => {
          _item.paymentHistory.map((_item_) => {
            if (_item_.transactionId === rrn) {
              _item_.refundStatus = status
            }
          })
        })
        return txn
      })
      own.subscriptions = JSON.parse(JSON.stringify([...trxn]))
    },

    // TODO: initiate api request params.
    initiateRefunds: flow(function* initiateRefunds(requestdata: any[]) {
      try {
        const refundData = requestdata[1]
        let selectedTxn: any = {}
        self.subscriptions.forEach((item: any) => {
          const txn = item
          item.transactionsHistory.map((transactionsHistory: any) => {
            transactionsHistory.paymentHistory.map((paymentHistory: any) => {
              if (paymentHistory.transactionId === self.selectedSubscription) {
                paymentHistory.refundAmount = refundData?.refundProcess
                selectedTxn = transactionsHistory
              }
            })
          })
          return txn
        })
        console.log(
          'SubscriptionHistory selectedTxn,  sourceSystem: ',
          JSON.parse(JSON.stringify(selectedTxn)),
          selectedTxn?.paymentHistory[0]?.sourceSystem,
        )
        const req = {
          source: getRefundSource(selectedTxn?.paymentHistory[0]?.sourceSystem),
          mid: RootStore.user.selectedMid,
          terminalId: RootStore.user.selectedTid,
          transactionTime: new Date(selectedTxn?.transactionDate),
          rrn: self.selectedSubscription,
          currency: 356,
          amount: refundData?.refundProcess,
          invoice: selectedTxn?.invoice ?? '',
          livemode: 'true',
        }
        let response
        response = yield initRefund(RootStore.user.session.getTokens, RootStore.user.session.userAccessToken, req)
        RootStore.subscriptions.updateRefundStatus(self.selectedSubscription, response?.data?.status ?? 'FAILED')
        return response
      } catch (e) {
        RootStore.subscriptions.updateRefundStatus(self.selectedSubscription, 'FAILED')
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      // return response
    }),
    updatePagination(selectedPage: number): void {
      self.pagination.number = selectedPage
    },
    resetPagination(): void {
      RootStore.transactions.filter.updateAllFieldsSelected(false)
      self.pagination = JSON.parse(JSON.stringify(paginationSettings))
    },
  }))

const SubscriptionHistoryData = SubscriptionHistory.create({
  subscriptions: [],
  pagination: paginationSettings,
  selectedDateInfo: {
    fromDate: getSelectedDate(0),
    toDate: new Date(Date.now()),
    position: {
      top: 0,
      left: 0,
      visible: false,
    },
    dateLabel: 'Today',
  },
})

export { SubscriptionHistory, SubscriptionHistoryData }
